/**
 * @fileoverview Search Form Component
 * @description Implements a search form with debounced input handling and form reset functionality
 *
 * Features:
 * - Debounced search input handling
 * - Smart form reset behavior
 * - Focus management
 * - Integration with predictive search
 *
 * HTML Structure Required:
 * - Form element with input[type="search"]
 * - Optional predictive search dropdown with [aria-selected] elements
 */

class SearchForm extends HTMLElement {
  /**
   * @constructor
   * @description Initializes the search form component
   * - Sets up input reference
   * - Configures form reset handler
   * - Sets up debounced input handler
   */
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  /**
   * @function shouldResetForm
   * @description Determines if the form should be reset based on predictive search state
   * @returns {boolean} True if no predictive search item is selected
   */
  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
    }
  }
}

customElements.define('search-form', SearchForm);
